import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import "./CategoryPage.scss";
import { allProducts, categoryBanners, Product } from "../Data/catalog";
import { addToCart, getCartItems } from "../Helper/cart";

function CategoryPage() {
  useEffect(() => {
    document.title = "INTER COMM | Category";
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  
  const { slug = "laptop" } = useParams();
  const navigate = useNavigate();

  const [selectedBrands, setSelectedBrands] = useState<string[]>([]);
  const [sortBy, setSortBy] = useState("default");
  const [cartCount, setCartCount] = useState(
    getCartItems().reduce((sum: any, item: any) => sum + item.quantity, 0)
  );

  const banner = categoryBanners[slug] ?? categoryBanners.laptop;

  const baseProducts = useMemo(() => {
    return allProducts.filter((item) => item.category === slug);
  }, [slug]);

  const brands = useMemo(() => {
    return Array.from(new Set(baseProducts.map((item: any) => item.brand)));
    }, [baseProducts]);

  const filteredProducts = useMemo(() => {
    let result = [...baseProducts];

    if (selectedBrands.length > 0) {
      result = result.filter((item) => selectedBrands.includes(item.brand));
    }

    if (sortBy === "price-asc") {
      result.sort(
        (a, b) =>
          Number(a.price.replace(/[^0-9.]/g, "")) -
          Number(b.price.replace(/[^0-9.]/g, ""))
      );
    }

    if (sortBy === "price-desc") {
      result.sort(
        (a, b) =>
          Number(b.price.replace(/[^0-9.]/g, "")) -
          Number(a.price.replace(/[^0-9.]/g, ""))
      );
    }

    if (sortBy === "name-asc") {
      result.sort((a, b) => a.title.localeCompare(b.title));
    }

    return result;
  }, [baseProducts, selectedBrands, sortBy]);

  const toggleBrand = (brand: string) => {
    setSelectedBrands((prev) =>
      prev.includes(brand)
        ? prev.filter((item) => item !== brand)
        : [...prev, brand]
    );
  };

  const handleAddToCart = (product: Product) => {
    const updated = addToCart(product);
    setCartCount(updated.reduce((sum: any, item: any) => sum + item.quantity, 0));
  };

  return (
    <section className="category-page">
      <div className="container">
        <div className="category-banner">
          <img src={banner.image} alt={slug} className="category-banner-image" />
        </div>

        <div className="category-toolbar">
          <div className="category-toolbar-left">
            <span>Showing {filteredProducts.length} item(s)</span>
          </div>

          <div className="category-toolbar-right">
            <Link to="/" className="home-btn">
              <i className="fa-solid fa-house" aria-hidden="true"></i>
            </Link>
            
            <label htmlFor="sortBy">Sort by:</label>
            <select
              id="sortBy"
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
            >
              <option value="default">Select</option>
              <option value="price-asc">Price Low to High</option>
              <option value="price-desc">Price High to Low</option>
              <option value="name-asc">Name A-Z</option>
            </select>
          </div>
        </div>

        <div className="category-layout">
          <aside className="category-filter">
            <h3>FILTER BY</h3>

            <div className="filter-block">
              <h4>Brands</h4>
              <ul>
                {brands.map((brand) => {
                  const count = baseProducts.filter((p) => p.brand === brand).length;

                  return (
                    <li key={brand}>
                      <label>
                        <input
                          type="checkbox"
                          checked={selectedBrands.includes(brand)}
                          onChange={() => toggleBrand(brand)}
                        />
                        <span>
                          {brand} ({count})
                        </span>
                      </label>
                    </li>
                  );
                })}
              </ul>
            </div>
          </aside>

          <div className="category-products">
            <div className="category-grid">
              {filteredProducts.map((product) => (
                <article className="category-product-card" key={product.id}>
                  <Link to={`/product/${product.id}`} className="category-product-image">
                    <img src={product.imgSrc} alt={product.title} />
                  </Link>

                  <div className="category-product-body">
                    <h3 title={product.title}>{product.title}</h3>
                    <div className="category-product-price">{product.price}</div>

                    <div className="category-product-actions">
                      <Link
                        to={`/product/${product.id}`}
                        className="view-details-btn"
                      >
                        View Details
                      </Link>

                      <button
                        type="button"
                        className="add-cart-btn"
                        onClick={() => handleAddToCart(product)}
                      >
                        <i className="fa-solid fa-cart-shopping" aria-hidden="true"></i>
                        Add to Cart
                      </button>
                    </div>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </div>
      </div>

      <button
        type="button"
        className="floating-cart-btn"
        onClick={() => navigate("/cart-summary")}
      >
        <i className="fa-solid fa-cart-shopping" aria-hidden="true"></i>
        <span className="floating-cart-label">Cart</span>
        {cartCount > 0 && <span className="floating-cart-count">{cartCount}</span>}
      </button>
    </section>
  );
}

export default CategoryPage;